import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MotionCta } from './homeAtmosphere';

export type StackStory = {
  id: string;
  tab: string;
  question: string;
  headline: string;
  points: string[];
  whyItMatters: string;
  viz: 'paid' | 'organic' | 'analytics' | 'store';
};

type Props = {
  stories: StackStory[];
  onCta: () => void;
};

const MotionBox = motion.create(Box);

const labelSx = {
  color: 'rgba(210, 218, 228, 0.45)',
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase' as const,
  marginBottom: '10px',
};

const VizShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      mt: '18px',
      p: '14px 16px',
      borderRadius: '14px',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      background: 'rgba(0, 0, 0, 0.28)',
      minHeight: 168,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}
  >
    {children}
    <Typography
      sx={{
        mt: '12px',
        fontSize: '0.62rem',
        color: 'rgba(210, 218, 228, 0.4)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}
    >
      Illustrative sample
    </Typography>
  </Box>
);

/** Paid: channel spend vs store revenue bars. */
const PaidViz: React.FC = () => {
  const rows = [
    { name: 'Meta spend', value: 72, color: 'rgba(30, 237, 159, 0.55)' },
    { name: 'Google spend', value: 48, color: 'rgba(30, 237, 159, 0.35)' },
    { name: 'Store revenue', value: 90, color: 'rgb(30, 237, 159)' },
  ];
  return (
    <VizShell>
      <Typography sx={{ ...labelSx, mb: '12px' }}>
        Spend vs store revenue
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {rows.map((row) => (
          <Box key={row.name}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: '4px',
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', color: '#a8b4c0' }}>
                {row.name}
              </Typography>
            </Box>
            <Box
              sx={{
                height: 8,
                borderRadius: '99px',
                background: '#1e2b34',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${row.value}%`,
                  borderRadius: '99px',
                  background: row.color,
                  transition: 'width 0.6s ease',
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </VizShell>
  );
};

/** Organic: query clicks vs paid clicks for same intent. */
const OrganicViz: React.FC = () => {
  const queries = [
    { q: 'brand + shoes', organic: 820, paid: 310 },
    { q: 'running sneakers', organic: 240, paid: 890 },
    { q: 'sale sneakers', organic: 90, paid: 640 },
  ];
  const max = 900;
  return (
    <VizShell>
      <Typography sx={{ ...labelSx, mb: '12px' }}>
        Organic vs paid clicks
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        {queries.map((row) => (
          <Box key={row.q}>
            <Typography
              sx={{ fontSize: '0.72rem', color: '#a8b4c0', mb: '5px' }}
            >
              {row.q}
            </Typography>
            <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <Box
                sx={{
                  height: 7,
                  width: `${(row.organic / max) * 100}%`,
                  borderRadius: '4px',
                  background: 'rgb(30, 237, 159)',
                  minWidth: 4,
                }}
                title={`Organic ${row.organic}`}
              />
              <Box
                sx={{
                  height: 7,
                  width: `${(row.paid / max) * 100}%`,
                  borderRadius: '4px',
                  background: 'rgba(30, 237, 159, 0.28)',
                  minWidth: 4,
                }}
                title={`Paid ${row.paid}`}
              />
            </Box>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '14px',
          mt: '10px',
          fontSize: '0.65rem',
          color: '#80919e',
        }}
      >
        <span>█ Organic</span>
        <span style={{ opacity: 0.7 }}>█ Paid</span>
      </Box>
    </VizShell>
  );
};

/** Analytics: source → orders (not sessions). */
const AnalyticsViz: React.FC = () => {
  const sources = [
    { name: 'Paid social', orders: 142, share: 38 },
    { name: 'Organic search', orders: 98, share: 26 },
    { name: 'Direct', orders: 76, share: 20 },
    { name: 'Email', orders: 58, share: 16 },
  ];
  return (
    <VizShell>
      <Typography sx={{ ...labelSx, mb: '12px' }}>
        Traffic sources → orders
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
        {sources.map((s) => (
          <Box
            key={s.name}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                sx={{ fontSize: '0.75rem', color: '#c5d0d8', mb: 0.5 }}
              >
                {s.name}
              </Typography>
              <Box
                sx={{
                  height: 6,
                  borderRadius: '99px',
                  background: '#1e2b34',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${s.share}%`,
                    background: 'rgb(30, 237, 159)',
                    borderRadius: '99px',
                  }}
                />
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 700,
                color: 'rgb(30, 237, 159)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {s.orders}
            </Typography>
          </Box>
        ))}
      </Box>
    </VizShell>
  );
};

/** Store: revenue vs contribution by channel (no oversell claims). */
const StoreViz: React.FC = () => {
  const channels = [
    { name: 'Paid', revenue: 100, contrib: 62 },
    { name: 'Organic', revenue: 70, contrib: 78 },
    { name: 'Other', revenue: 45, contrib: 55 },
  ];
  return (
    <VizShell>
      <Typography sx={{ ...labelSx, mb: '12px' }}>
        Revenue vs contribution
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px',
          alignItems: 'end',
          minHeight: 88,
        }}
      >
        {channels.map((c) => (
          <Box key={c.name} sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                gap: '3px',
                justifyContent: 'center',
                alignItems: 'flex-end',
                height: 72,
                mb: '6px',
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: `${c.revenue}%`,
                  borderRadius: '4px 4px 0 0',
                  background: 'rgba(30, 237, 159, 0.35)',
                }}
              />
              <Box
                sx={{
                  width: 14,
                  height: `${c.contrib}%`,
                  borderRadius: '4px 4px 0 0',
                  background: 'rgb(30, 237, 159)',
                }}
              />
            </Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#a8b4c0' }}>
              {c.name}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '14px',
          mt: '8px',
          fontSize: '0.65rem',
          color: '#80919e',
          justifyContent: 'center',
        }}
      >
        <span>Revenue</span>
        <span style={{ color: 'rgb(30, 237, 159)' }}>Contribution</span>
      </Box>
    </VizShell>
  );
};

const StoryViz: React.FC<{ kind: StackStory['viz'] }> = ({ kind }) => {
  switch (kind) {
    case 'paid':
      return <PaidViz />;
    case 'organic':
      return <OrganicViz />;
    case 'analytics':
      return <AnalyticsViz />;
    case 'store':
      return <StoreViz />;
    default:
      return null;
  }
};

/**
 * Stack explorer — question + illustrative viz + what Kipify shows.
 */
export const HomeStackExplorer: React.FC<Props> = ({ stories, onCta }) => {
  const reduce = useReducedMotion();
  const [activeId, setActiveId] = useState(stories[0]?.id ?? '');
  const active = stories.find((s) => s.id === activeId) ?? stories[0];

  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (stories.some((s) => s.id === hash)) {
        setActiveId(hash);
      }
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, [stories]);

  if (!active) return null;

  return (
    <Box
      id="stack"
      sx={{
        padding: { xs: '64px 6%', md: '88px 10%' },
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ maxWidth: 1100, margin: '0 auto' }}>
        <Typography
          sx={{
            textAlign: 'center',
            color: 'rgba(210, 218, 228, 0.55)',
            fontSize: '0.78rem',
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            marginBottom: '12px',
          }}
        >
          From data to decisions
        </Typography>
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
            color: 'white',
            fontSize: { xs: '1.85rem', md: '2.4rem' },
            fontWeight: 700,
            letterSpacing: '-0.03em',
            marginBottom: '28px',
          }}
        >
          Pick a question. See the bigger picture.
        </Typography>

        <Box
          role="tablist"
          aria-label="Product stack areas"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '36px',
          }}
        >
          {stories.map((story) => {
            const selected = story.id === active.id;
            return (
              <Box
                key={story.id}
                component="button"
                role="tab"
                type="button"
                aria-selected={selected}
                id={story.id}
                onClick={() => {
                  setActiveId(story.id);
                  window.history.replaceState(null, '', `#${story.id}`);
                }}
                sx={{
                  cursor: 'pointer',
                  border: selected
                    ? '1px solid rgba(30, 237, 159, 0.65)'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  background: selected
                    ? 'rgba(30, 237, 159, 0.12)'
                    : 'rgba(14, 20, 30, 0.55)',
                  color: selected
                    ? 'rgb(30, 237, 159)'
                    : 'rgba(230, 234, 240, 0.82)',
                  borderRadius: '999px',
                  padding: '9px 16px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition:
                    'border-color 0.2s ease, background 0.2s ease, color 0.2s ease',
                  '&:hover': {
                    borderColor: 'rgba(30, 237, 159, 0.45)',
                    color: 'rgb(30, 237, 159)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #5b9fff',
                    outlineOffset: '3px',
                  },
                }}
              >
                {story.tab}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '20px',
            background:
              'linear-gradient(145deg, rgba(12, 18, 28, 0.95) 0%, rgba(7, 11, 17, 0.98) 100%)',
            boxShadow: '0 28px 80px rgba(0, 0, 0, 0.35)',
            overflow: 'hidden',
            /* Stable height across tabs — avoids jump on switch */
            minHeight: { xs: 560, md: 480 },
          }}
        >
          <AnimatePresence mode="wait">
            <MotionBox
              key={active.id}
              role="tabpanel"
              aria-labelledby={active.id}
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reduce ? undefined : { opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 0,
                minHeight: { xs: 560, md: 480 },
              }}
            >
              {/* Left: question + viz (mobile order 1) */}
              <Box
                sx={{
                  p: { xs: '24px 20px', md: '32px 36px' },
                  borderRight: {
                    xs: 'none',
                    md: '1px solid rgba(255, 255, 255, 0.07)',
                  },
                  borderBottom: {
                    xs: '1px solid rgba(255, 255, 255, 0.07)',
                    md: 'none',
                  },
                  background: 'rgba(255, 255, 255, 0.015)',
                  display: 'flex',
                  flexDirection: 'column',
                  order: 1,
                }}
              >
                <Typography sx={labelSx}>The question</Typography>
                <Typography
                  sx={{
                    color: 'rgba(230, 234, 240, 0.92)',
                    fontSize: { xs: '1.15rem', md: '1.28rem' },
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.35,
                  }}
                >
                  {active.question}
                </Typography>
                <StoryViz kind={active.viz} />
              </Box>

              {/* Right: what Kipify shows (mobile order 2) */}
              <Box
                sx={{
                  p: { xs: '24px 20px', md: '32px 36px' },
                  background:
                    'radial-gradient(420px 200px at 100% 0%, rgba(30, 237, 159, 0.1), transparent 55%)',
                  display: 'flex',
                  flexDirection: 'column',
                  order: 2,
                }}
              >
                <Typography
                  sx={{
                    ...labelSx,
                    color: 'rgb(30, 237, 159)',
                  }}
                >
                  What Kipify shows
                </Typography>
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: { xs: '1.15rem', md: '1.32rem' },
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.3,
                    marginBottom: '16px',
                  }}
                >
                  {active.headline}
                </Typography>

                <Box
                  component="ul"
                  sx={{ listStyle: 'none', m: 0, p: 0, mb: '20px' }}
                >
                  {active.points.map((line) => (
                    <Box
                      component="li"
                      key={line}
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-start',
                        py: '9px',
                        borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                        '&:last-of-type': {
                          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          mt: '7px',
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          flexShrink: 0,
                          backgroundColor: 'rgb(30, 237, 159)',
                        }}
                        aria-hidden
                      />
                      <Typography
                        sx={{
                          color: 'rgba(210, 218, 228, 0.78)',
                          fontSize: '0.92rem',
                          lineHeight: 1.55,
                        }}
                      >
                        {line}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Typography sx={labelSx}>Why it matters</Typography>
                <Typography
                  sx={{
                    color: 'rgba(210, 218, 228, 0.72)',
                    fontSize: '0.9rem',
                    lineHeight: 1.55,
                  }}
                >
                  {active.whyItMatters}
                </Typography>
              </Box>
            </MotionBox>
          </AnimatePresence>
        </Box>

        {/* Single CTA — outside tab panel so it doesn’t remount */}
        <Box sx={{ textAlign: 'center', mt: '28px' }}>
          <MotionCta onClick={onCta}>Start free trial</MotionCta>
        </Box>
      </Box>
    </Box>
  );
};
